import { useEffect } from 'react';
import moment from 'moment';
import { useRouter } from '@blocs.router';
import {
  WORKSHEET_FILTERS_SESSION_STORAGE_KEY,
  FiltersNameEnum,
  RangeEnum,
  useRequestTypeOptions,
  useViewByDateOptions,
  emptyOption,
} from '@blocs.filter';
import { gqlRepWorksheetFieldOrder } from '@minecraft.graphql-types';
import { refreshTokenApiCall } from '../../apollo';
import { clearStorage, redirectToLogin, setRefreshToken } from '../../utils';
import { QUERY_PARAMS } from '../../constants';

type Props = {
  redirectTo: string;
};

export const routeAuthenticatorRouteProps = {
  exact: true,
  path: '/authenticate/:refreshToken',
};

const useDefaultWorksheetFilters = () => {
  const viewByDateOptions = useViewByDateOptions();

  return {
    [FiltersNameEnum.requestType]: emptyOption,
    [FiltersNameEnum.slotDateRange]: {
      [FiltersNameEnum.min]: null,
      [FiltersNameEnum.max]: null,
      type: RangeEnum.date,
    },
    [FiltersNameEnum.requestStatus]: [],
    [FiltersNameEnum.projectName]: [],
    [FiltersNameEnum.talent]: [],
    [FiltersNameEnum.castingDirector]: [],
    [FiltersNameEnum.viewByDate]: viewByDateOptions.find(
      (option) => option.value === gqlRepWorksheetFieldOrder.receivedAt
    ),
    [FiltersNameEnum.dueRange]: {
      [FiltersNameEnum.min]: moment().startOf('day'),
      [FiltersNameEnum.max]: moment().startOf('day').add(14, 'days').toDate(),
      type: RangeEnum.date,
    },
    [FiltersNameEnum.receivedRange]: {
      [FiltersNameEnum.min]: moment().startOf('day').subtract(7, 'days').toDate(),
      [FiltersNameEnum.max]: moment().startOf('day'),
      type: RangeEnum.date,
    },
  };
};

const normalizeSlotDate = (date, scheduleId) => {
  if (!date || scheduleId) return date;

  return date.replace('Z', '');
};

export const useRepsWorksheetFiltersFromQuery = () => {
  const requestTypeOptions = useRequestTypeOptions();
  const defaultWorksheetFilters = useDefaultWorksheetFilters();
  const viewByDateOptions = useViewByDateOptions();

  return (queryParams): void => {
    const requestTypeFromQuery = queryParams.get(QUERY_PARAMS.requestType);
    const projectIdsFromQuery = queryParams.get(QUERY_PARAMS.projectIds)?.split(',') || [];
    const slotDateToFromQuery = queryParams.get(QUERY_PARAMS.slotDateTo);
    const slotDateFromFromQuery = queryParams.get(QUERY_PARAMS.slotDateFrom);
    const mediaQuestionDueDateToFromQuery = queryParams.get(QUERY_PARAMS.dueDateTo);
    const mediaQuestionDueDateFromFromQuery = queryParams.get(QUERY_PARAMS.dueDateFrom);
    const scheduleIdFromFromQuery = queryParams.get(QUERY_PARAMS.scheduleId);

    const shouldApplyFiltersFromQueryParams = requestTypeFromQuery || projectIdsFromQuery.length > 0;

    const additionalFields = {
      [FiltersNameEnum.slotDateRange]: { min: null, max: null, type: 'date' },
      [FiltersNameEnum.dueRange]: { min: null, max: null, type: 'date' },
    };

    if (slotDateToFromQuery || slotDateFromFromQuery) {
      additionalFields[FiltersNameEnum.slotDateRange] = {
        [FiltersNameEnum.min]: slotDateFromFromQuery
          ? normalizeSlotDate(slotDateFromFromQuery, scheduleIdFromFromQuery)
          : null,
        [FiltersNameEnum.max]: slotDateToFromQuery
          ? normalizeSlotDate(slotDateToFromQuery, scheduleIdFromFromQuery)
          : null,
        type: 'date',
      };
    }

    if (mediaQuestionDueDateToFromQuery || mediaQuestionDueDateFromFromQuery) {
      additionalFields[FiltersNameEnum.dueRange] = {
        [FiltersNameEnum.min]: mediaQuestionDueDateFromFromQuery
          ? normalizeSlotDate(mediaQuestionDueDateFromFromQuery, scheduleIdFromFromQuery)
          : null,
        [FiltersNameEnum.max]: mediaQuestionDueDateToFromQuery
          ? normalizeSlotDate(mediaQuestionDueDateToFromQuery, scheduleIdFromFromQuery)
          : null,
        type: 'date',
      };
      additionalFields[FiltersNameEnum.viewByDate] = viewByDateOptions.find(
        (option) => option.value === gqlRepWorksheetFieldOrder.dueDate
      );
    }

    if (shouldApplyFiltersFromQueryParams && !!Object.keys(additionalFields).length) {
      const worksheetFilterParams = {
        ...defaultWorksheetFilters,
        [FiltersNameEnum.requestType]: {
          value: requestTypeFromQuery,
          label: requestTypeOptions.find((req) => req.value === requestTypeFromQuery).label,
        },
        [FiltersNameEnum.projectName]:
          projectIdsFromQuery?.map((projId) => ({
            value: projId,
            label: '1 selected',
          })) || [],
        ...additionalFields,
      };

      sessionStorage.setItem(WORKSHEET_FILTERS_SESSION_STORAGE_KEY, JSON.stringify(worksheetFilterParams));
    }
  };
};

const useAuthenticate = (refreshToken?: string) => {
  const authenticate = async () => {
    if (!refreshToken) {
      clearStorage();
      redirectToLogin(false);

      return;
    }

    try {
      setRefreshToken(refreshToken);
      const refreshRes = await refreshTokenApiCall({});

      if (!refreshRes?.data?.refreshToken?.access || !refreshRes?.data?.refreshToken?.refresh) {
        throw refreshRes?.errors?.[0] || new Error('failed to exchange refresh token');
      }
    } catch (err) {
      console.error('useAuthenticate error', { err });
      clearStorage();
      redirectToLogin(false);
    }
  };

  return { authenticate };
};

const jsonParseQueryParam = (params: URLSearchParams, key: string) => {
  const value = params.get(key);

  try {
    return JSON.parse(params.get(key));
  } catch (err) {
    console.error('could not parse query param value to JSON', { err, key, value });

    return undefined;
  }
};

const RouteAuthenticator = ({ redirectTo }: Props) => {
  const {
    routeParams: { refreshToken: tokenToRefresh },
    search,
    navigate,
  } = useRouter<{ refreshToken?: string }>();
  const queryParams = new URLSearchParams(search);
  const backUrl = queryParams.get(QUERY_PARAMS.redirectTo);

  const handleRepsWorksheetFiltersFromQuery = useRepsWorksheetFiltersFromQuery();

  const isFromOnboarding = jsonParseQueryParam(queryParams, 'isFromOnboarding');
  const { authenticate } = useAuthenticate(tokenToRefresh);

  useEffect(() => {
    authenticate()
      .then(() => {
        queryParams.delete(QUERY_PARAMS.redirectTo);
        if (backUrl && backUrl.length > 0 && !backUrl.startsWith('/authenticate')) {
          handleRepsWorksheetFiltersFromQuery(queryParams);
          // Use the `redirectUrl` as the actual url but maintain any other query params we got while signing in
          navigate({ to: `${backUrl}?${queryParams.toString()}` });
        } else {
          const redirectQueryParams = isFromOnboarding ? queryParams.toString() : '';

          navigate({ to: `${redirectTo}?${redirectQueryParams}` });
        }
      })
      .catch((e) => {
        console.error('error authenticating token', { e });
        clearStorage();
        redirectToLogin();
      });
  }, []);

  return null;
};

export default RouteAuthenticator;
